import { add, TimeUnit, toISO8601UTC } from '@fcom/core/utils/date';
import { LocalDate, TzDate } from '@fcom/core/utils/tz-date';
import { FinnairOrder } from '@fcom/dapi/api/models/finnair-order';
import { FinnairPassengerCode } from '@fcom/dapi/api/models/finnair-passenger-code';
import { FinnairTravelEndpoint } from '@fcom/dapi/api/models/finnair-travel-endpoint';

import {
  CarTrawlerBrowserClientID,
  CarTrawlerClientID,
  CarTrawlerNativeClientID,
  ExpediaHotelOrigin,
  ThirdPartyAncillariesProvider,
  TouchPoint,
} from '../enums';

const REMOVE_TIMEZONE = /(Z|\+|-)(\d|:){0,5}$/;
const DEFAULT_STAY = 7;
export const removeTimezone = (dateTime: string): string => {
  return dateTime.replace(REMOVE_TIMEZONE, 'Z');
};

const isOrderWithinHours = (order: FinnairOrder, hours = 24, inclusive = true): boolean => {
  if (!order?.creationDateTime) {
    return false;
  }
  const orderCreationDate = new Date(order.creationDateTime);
  const now = new Date();
  const diffHours = (now.getTime() - orderCreationDate.getTime()) / (1000 * 60 * 60);
  return inclusive ? diffHours <= hours : diffHours < hours;
};

export const isOrderWithin24HoursOfCreation = (order: FinnairOrder): boolean => {
  return isOrderWithinHours(order, 24, true);
};

export const isOrderLessThan24HoursOfCreation = (order: FinnairOrder): boolean => {
  return isOrderWithinHours(order, 24, false);
};

const carTrawlerRentalLangMap = {
  'dk-da': 'da',
  'de-de': 'de',
  'ch-de': 'ch',
  'at-de': 'de',
  'ee-et': 'et',
  'us-en': 'en-us',
  'es-es': 'es',
  'fr-fr': 'fr',
  'ca-fr': 'fr-ca',
  'ch-fr': 'fr',
  'it-it': 'it',
  'no-no': 'no',
  'pl-pl': 'pl',
  'fi-fi': 'fi',
  'fi-sv': 'sv',
  'se-sv': 'sv',
  'ru-ru': 'ru',
  'kr-ko': 'ko',
  'jp-ja': 'ja',
  'hk-zh': 'zh-hk',
  'cn-zh': 'zh',
};

const carRentalTransferLangMap = {
  'dk-da': 'da-DK',
  'ch-de': 'de-CH',
  'de-de': 'de-DE',
  'gb-en': 'en-GB',
  'us-en': 'en-US',
  'es-es': 'es-ES',
  'ee-et': 'et-EE',
  'fi-fi': 'fi-FI',
  'fi-sv': 'sv-SE',
  'ca-fr': 'fr-CA',
  'fr-fr': 'fr-FR',
  'it-it': 'it-IT',
  'jp-ja': 'ja-JP',
  'kr-ko': 'ko-KR',
  'no-no': 'no-NO',
  'pl-pl': 'pl-PL',
  'ru-ru': 'ru-RU',
  'se-sv': 'sv-SE',
  'cn-zh': 'zh-CN',
  'hk-zh': 'zh-HK',
  'tw-zh': 'zh-TW',
};

const carRentalSmartBlockLangMap = {
  ...carRentalTransferLangMap,
};

export const carTrawlerCountryLangMap = {
  [ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL]: carTrawlerRentalLangMap,
  [ThirdPartyAncillariesProvider.CAR_TRAWLER_TRANSFER]: carRentalTransferLangMap,
  [ThirdPartyAncillariesProvider.CAR_TRAWLER_SMARTBLOCK]: carRentalSmartBlockLangMap,
};

export const carTrawlerSupportedCurrencies = [
  'EUR',
  'SEK',
  'DKK',
  'NOK',
  'CHF',
  'PLN',
  'HUF',
  'CZK',
  'GBP',
  'USD',
  'AUD',
  'CAD',
  'HKD',
  'CNY',
  'SGD',
  'JPY',
  'KRW',
  'INR',
  'THB',
];

export const getCarTrawlerLang = (provider: ThirdPartyAncillariesProvider, finnairLocale: string): string => {
  return carTrawlerCountryLangMap[provider]?.[finnairLocale] || 'en';
};

export const ENG_LANGUAGE_QUERY_PARAM = 'langid=2057';
export const getExpediaHotelLang = (finnairLocale: string): { urlPrefix: string; additionalQueryParam?: string } =>
  ({
    'fi-fi': { urlPrefix: 'fi', additionalQueryParam: 'locale=fi_FI' },
    'fi-en': { urlPrefix: 'fi', additionalQueryParam: ENG_LANGUAGE_QUERY_PARAM },
    'se-sv': { urlPrefix: 'se' },
    'se-en': { urlPrefix: 'se', additionalQueryParam: ENG_LANGUAGE_QUERY_PARAM },
    'de-de': { urlPrefix: 'de' },
    'de-en': { urlPrefix: 'de', additionalQueryParam: ENG_LANGUAGE_QUERY_PARAM },
    'at-de': { urlPrefix: 'de' },
    'ch-de': { urlPrefix: 'de' },
    'gb-en': { urlPrefix: 'uk' },
  })[finnairLocale] || { urlPrefix: 'ie' };

// MDPCID is mapped in GA, should be found from GA under variable marketing_same_session_marketing_code
export const getExpediaHotelMDPCIDLang = (finnairLocale: string): string =>
  ({
    'fi-fi': 'FI_FI',
    'fi-en': 'EN_FI',
    'se-sv': 'SV_SE',
    'se-en': 'EN_SE',
    'de-de': 'DE_DE',
    'de-en': 'EN_DE',
    'at-de': 'DE_DE',
    'ch-de': 'DE_DE',
    'gb-en': 'EN_UK',
  })[finnairLocale] || 'EN_IE';

export const supportedCarTrawlerCurrencies = (finnairCurrency: string): string => {
  return carTrawlerSupportedCurrencies.includes(finnairCurrency) ? finnairCurrency : 'EUR';
};

const expediaHotelBannerOriginLabel: Record<ExpediaHotelOrigin, string> = {
  [ExpediaHotelOrigin.WIDGET_HOTEL_TAB]: ExpediaHotelOrigin.WIDGET_HOTEL_TAB,
  [ExpediaHotelOrigin.TRIP_OVERVIEW]: 'Trip-Overview',
  [ExpediaHotelOrigin.THIRD_PARTY_ANCILLARIES]: 'Destination-Service-Tab',
  [ExpediaHotelOrigin.ANCILLARY_CONFIRMATION]: 'Manage-Booking-Conf-Page',
};

export const getExpediaHotelMDPCID = (finnairLocale: string, origin: ExpediaHotelOrigin): string => {
  return `Finnair-${getExpediaHotelMDPCIDLang(finnairLocale)}.DPS.Finnair.${expediaHotelBannerOriginLabel[origin]}-Storefront.HOTEL`;
};

/**
 * Manipulate an ISO 8601 dateTime string into the format that CarTrawler requires.
 *
 * Example format that CarTrawler requires: 2024-06-20T05:15:00
 *
 * 1) CarTrawler wants local datetime, but no timezone in the string.
 * 2) CarTrawler transfers.finnair.com site does not allow milliseconds in the string
 */
export const getCarTrawlerDateTime = (dateTime: string, addHours: number): string => {
  return new TzDate(removeTimezone(dateTime)).addHours(addHours).toISOString().split('.')[0];
};

const reduceAdultsAndChildren = (
  order: FinnairOrder,
  arriving: FinnairTravelEndpoint
): { adults: number; children: number[] } => {
  const millisecondsToYears = (milliseconds: number): number => milliseconds / (365.25 * 24 * 60 * 60 * 1000);
  const arrivalDate = arriving?.dateTime ? new Date(arriving.dateTime) : new Date();

  return order.passengers
    .filter((passenger) => passenger.passengerTypeCode === FinnairPassengerCode.ADT || passenger.dateOfBirth)
    .reduce<{
      adults: number;
      children: number[];
    }>(
      (acc, passenger) => {
        if (passenger.passengerTypeCode === FinnairPassengerCode.ADT) {
          return { ...acc, adults: acc.adults + 1 };
        } else if (passenger.dateOfBirth) {
          const birthDate = new Date(passenger.dateOfBirth);
          const childAge = Math.floor(millisecondsToYears(arrivalDate.getTime() - birthDate.getTime()));
          return { ...acc, children: [...acc.children, childAge] };
        }
        return acc;
      },
      { adults: 0, children: [] }
    );
};

const createExpediaRoomSplitForChildren = (children: number[], roomNumber: number): string => {
  return (
    `&NumChild${roomNumber}=${children.length}` +
    children.map((childAge, i) => `&Rm${roomNumber}Child${i + 1}Age=${childAge}`).join('')
  );
};

const getExpediaRoomSplit = (order: FinnairOrder, arriving: FinnairTravelEndpoint): string => {
  const MAX_AMOUNT_OF_CHILDREN_PER_ROOM = 6;
  const { adults, children } = reduceAdultsAndChildren(order, arriving);

  if (
    adults === 0 ||
    // If no arrival date, we cannot count children age
    (!arriving?.dateTime && children.length > 0) ||
    // Max amount of CHD in a room is 6, so if there is more than 6 CHD per ADT, we do not provide room split.
    (children.length > 0 && children.length / adults > MAX_AMOUNT_OF_CHILDREN_PER_ROOM)
  ) {
    return '';
  }

  if (children.length <= 6) {
    return `&NumRoom=1&NumAdult1=${adults}${children.length ? createExpediaRoomSplitForChildren(children, 1) : ''}`;
  }

  return `&NumRoom=2&NumAdult1=${adults - 1}${createExpediaRoomSplitForChildren(children.slice(0, 6), 1)}&NumAdult2=1${createExpediaRoomSplitForChildren(children.slice(6), 2)}`;
};

const getExpediaHotelDateString = (arriving: FinnairTravelEndpoint, leaving: FinnairTravelEndpoint): string => {
  return arriving?.dateTime && leaving?.dateTime
    ? `&InDate=${TzDate.of(arriving.dateTime).toLocalDate()}&OutDate=${TzDate.of(leaving.dateTime).toLocalDate()}`
    : '';
};

const getExpediaHotelMDPCIDParam = (lang: string, origin: ExpediaHotelOrigin): string => {
  return `&MDPCID=${getExpediaHotelMDPCID(lang, origin)}`;
};

export const buildExpediaHotelDeeplink = (
  order: FinnairOrder,
  arriving: FinnairTravelEndpoint,
  leaving: FinnairTravelEndpoint,
  lang: string,
  origin: ExpediaHotelOrigin
): string => {
  const { urlPrefix, additionalQueryParam } = getExpediaHotelLang(lang);
  const city = `CityName=${order.locations[arriving.locationCode].cityName}`;
  const dates = getExpediaHotelDateString(arriving, leaving);
  const roomSplit = getExpediaRoomSplit(order, arriving);
  const mdpcId = getExpediaHotelMDPCIDParam(lang, origin);

  return `https://${urlPrefix}.hotels.finnair.com/go/hotel/search/Destination?${city}${dates}${roomSplit}${mdpcId}${additionalQueryParam ? '&' + additionalQueryParam : ''}`;
};

export const buildExpediaActivitiesDeepLink = (
  order: FinnairOrder,
  lang: string,
  expediaWhiteLabelSiteUrlTemplate: string,
  touchpoint: TouchPoint
): string => {
  const activitiesLangInfo = getExpediaActivitiesLang(lang);
  const airportCode = order.bounds[0].arrival.locationCode;
  const airportInformation = order.locations[airportCode];
  const location = `${airportInformation.cityName ?? airportInformation.cityCode ?? airportCode},${airportInformation.countryCode}`;
  const destinationArrivalDate = new Date(order.bounds[0].arrival.dateTime);
  const startDate = toISO8601UTC(destinationArrivalDate);
  const endDate =
    order.bounds.length === 1
      ? toISO8601UTC(add(destinationArrivalDate, DEFAULT_STAY, TimeUnit.day))
      : toISO8601UTC(new Date(order.bounds[1].departure.dateTime));
  const mdpcid = getExpediaMDPCIdForTouchpoint(touchpoint, activitiesLangInfo.urlPrefix.toUpperCase());
  const rootPath = expediaWhiteLabelSiteUrlTemplate
    .replaceAll('{lang}', activitiesLangInfo.urlPrefix)
    .concat(`/things-to-do/search`);
  const fullUrl = new URL(rootPath);
  fullUrl.searchParams.set('location', location);
  fullUrl.searchParams.set('startDate', startDate);
  fullUrl.searchParams.set('endDate', endDate);
  fullUrl.searchParams.set('MDPCID', mdpcid);
  fullUrl.searchParams.set('locale', activitiesLangInfo.expediaLocale);
  return fullUrl.toString();
};

export const getExpediaActivitiesLang = (finnairLocale: string): { urlPrefix: string; expediaLocale: string } => {
  return (
    (finnairLocale &&
      {
        'fi-fi': { urlPrefix: 'fi', expediaLocale: 'fi_FI' },
        'fi-en': { urlPrefix: 'fi', expediaLocale: 'en_GB' },
        'se-sv': { urlPrefix: 'se', expediaLocale: 'sv_SE' },
        'se-en': { urlPrefix: 'se', expediaLocale: 'en_GB' },
        'de-de': { urlPrefix: 'de', expediaLocale: 'de_DE' },
        'de-en': { urlPrefix: 'de', expediaLocale: 'en_GB' },
        'at-de': { urlPrefix: 'de', expediaLocale: 'de_DE' },
        'ch-de': { urlPrefix: 'de', expediaLocale: 'de_DE' },
        'gb-en': { urlPrefix: 'uk', expediaLocale: 'en_GB' },
      }[finnairLocale.toLocaleLowerCase()]) || { urlPrefix: 'ie', expediaLocale: 'en_GB' }
  );
};

export const getExpediaMDPCIdForTouchpoint = (touchpoint: TouchPoint, lang: string): string => {
  switch (touchpoint) {
    case TouchPoint.MMB_OVERVIEW:
      return `Finnair-${lang}.DPS.Finnair.MMB-Trips.Activities`;
    case TouchPoint.MMB_HOTEL_AND_EXPERIENCES:
      return `Finnair-${lang}.DPS.Finnair.MMB-SubPage.Activities`;
    case TouchPoint.MMB_CONFIRMATION:
      return `Finnair-${lang}.DPS.Finnair.MMB-ConfPage.Activities`;
    case TouchPoint.CHECK_IN_CONFIRMATION:
      return `Finnair-${lang}.DPS.Finnair.ConfPage-Checkin.Activities`;
    default:
      return `Finnair-${lang}.DPS.Finnair.MMB-Trips.Activities`;
  }
};

/**
 * Manipulate an ISO 8601 dateTime string (2025-07-20T23:20:00+03:00) into the format that Expedia requires.
 *
 * Example format that Expedia requires: 2024-08-29T05:54:02
 */
export const getExpediaDateTime = (dateTime: string, addHours = 0): string => {
  return new TzDate(removeTimezone(dateTime)).addHours(addHours).toISOString().split('.')[0];
};

/** By given date of birth in format 2012-02-12, returns the age in years. */
export const getChildAge = (dateOfBirth: string, arrivalDate: Date | string): number => {
  if (!dateOfBirth) {
    throw new Error('dateOfBirth is required');
  }

  const arrivalLocal: LocalDate =
    arrivalDate instanceof Date ? LocalDate.forDateObj(arrivalDate) : TzDate.of(arrivalDate).toLocalDate();

  const birthLocal: LocalDate = TzDate.of(dateOfBirth).toLocalDate();

  let age = arrivalLocal.localYear - birthLocal.localYear;

  const birthdayNotOccurredYet =
    arrivalLocal.localMonth < birthLocal.localMonth ||
    (arrivalLocal.localMonth === birthLocal.localMonth && arrivalLocal.localDay < birthLocal.localDay);

  if (birthdayNotOccurredYet) {
    age--;
  }

  return age;
};

export function getCarTrawlerClientId(
  clientIdKey: keyof typeof CarTrawlerNativeClientID,
  isInsideNativeWebView: boolean
): CarTrawlerClientID {
  return isInsideNativeWebView ? CarTrawlerNativeClientID[clientIdKey] : CarTrawlerBrowserClientID[clientIdKey];
}
